import { RoutingLink } from "#batteries-included-components/RoutingLink";
import {
  useListFlowProductCategories,
  useListFlowProductTypes,
  useListFlowTypes,
} from "#hooks/adapters/useFlows";
import { linkToFacilities } from "#routers/links";
import { linkToEquipmentDetail } from "#routes/organization/equipment/[equipmentId]";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import { displayValueByDataType } from "#src/utils/display";
import { KeyValuePanel } from "@validereinc/common-components";
import { AssetType, type FlowType } from "@validereinc/domain";
import React, { ReactNode } from "react";

export const FlowDetailsPanel = ({
  flow,
  isLoading,
  templatedConfigurationLink,
}: {
  flow?: FlowType;
  isLoading: boolean;
  templatedConfigurationLink: ReactNode;
}) => {
  const flowTypesQuery = useListFlowTypes();
  const productCategoriesQuery = useListFlowProductCategories();
  const productTypesQuery = useListFlowProductTypes();
  const customAttributes =
    useListCustomAttributes({ filters: { entity_type: AssetType.FLOW } }).data
      ?.data ?? [];

  return (
    <KeyValuePanel
      panelMaxColumnCount={3}
      panelKeyValueListProps={{ maxRowCount: 6, variant: "shaded" }}
      panelProps={{
        isFluidY: false,
        loaded: !isLoading,
        style: { marginBottom: 0 },
      }}
      data={[
        ...(templatedConfigurationLink
          ? [
              {
                title: "Asset Builder",
                value: templatedConfigurationLink,
              },
            ]
          : []),
        {
          title: "Product Type",

          value: productTypesQuery.data?.data.find(
            ({ id }) => id === flow?.product_type
          )?.name,
        },
        {
          title: "Flow Type",
          value: flowTypesQuery.data?.data?.find(({ id }) => id === flow?.type)
            ?.name,
        },
        {
          title: "Product Category",
          value: productCategoriesQuery.data?.data.find(
            ({ id }) => id === flow?.product_category
          )?.name,
        },
        {
          title: "Associated Facility",
          value: flow?.associated_facility_id ? (
            <RoutingLink to={linkToFacilities(flow?.associated_facility_id)}>
              {flow?.associated_facility?.name ?? "-"}
            </RoutingLink>
          ) : (
            "-"
          ),
        },
        {
          title: "Associated Equipment",
          value: flow?.associated_equipment_id ? (
            <RoutingLink
              to={linkToEquipmentDetail(flow?.associated_equipment_id)}
            >
              {flow?.associated_equipment?.name ?? "-"}
            </RoutingLink>
          ) : (
            "-"
          ),
        },
        ...(customAttributes
          ?.filter(
            ({ entity_subtypes }) =>
              !entity_subtypes?.length ||
              entity_subtypes.includes(flow?.type ?? "")
          )
          .map(({ display_name, field_name, data_type }) => {
            const value = flow?.custom_attributes?.[field_name];

            return {
              title: display_name,
              value: displayValueByDataType(value, data_type, {
                alignment: "right",
              }),
            };
          }) ?? []),
      ]}
    />
  );
};
